import { useEffect, useRef } from "react";

/**
 * Fires `run` only when `signature` differs from the last value it fired for —
 * seeded with the MOUNT signature, so the initial render never fires.
 *
 * This is the shared dedupe for data providers that paint from a
 * host-prefetched `initialX` prop: the host already issued the mount request,
 * so the provider's own fetch must fire only when the request it WOULD send
 * differs from the one on screen. Replaces the boolean `isFirstRender` ref
 * guard, which broke two ways:
 *  - StrictMode's dev effect replay re-runs the effect with the ref already
 *    consumed → duplicate mount fetch + loading flash on every dev mount;
 *  - dep arrays keyed on context-object IDENTITY (e.g. `allFields` from
 *    useFields(), rebuilt content-equal whenever FieldsProvider's derived
 *    query layers resolve) re-fired the fetch for byte-identical requests.
 *
 * Rules for building `signature` (JSON.stringify of the request):
 *  - MIRROR THE REQUEST EXACTLY, including fallback expressions (e.g.
 *    `parseSortByApiRequest(...) ?? sortBy`), and coalesce every
 *    potentially-`undefined` member to `null` — JSON.stringify DROPS
 *    `undefined` keys, which can collapse two distinct requests into one
 *    signature and swallow a real change.
 *  - PARSE FRESH PER USE: never share one parsed-filters array between the
 *    signature and a request handler — some handlers mutate their `filter`
 *    argument (getGuestsToBeReviewed pushes a row), so a shared array would
 *    grow per fetch → signature drift → runaway fetch loop.
 *  - Include forced-refresh counters (refreshCounter etc.) so imperative
 *    refreshes still fire on an otherwise-identical request.
 *
 * Transition-agnostic: callers wrap their own startTransition/setLoading
 * inside `run`. `run` is kept in a ref so the effect always invokes the
 * latest render's closure (same semantics as the runFetchRef pattern in
 * GuestsProvider).
 */
export function useSignatureEffect(signature: string, run: () => void) {
  const runRef = useRef(run);
  runRef.current = run;
  const lastFiredRef = useRef(signature);
  useEffect(() => {
    if (lastFiredRef.current === signature) return;
    lastFiredRef.current = signature;
    runRef.current();
  }, [signature]);
}
